<template>
  <panel :height="calculatedHeight">
    <template v-slot:header>
      <!-- Use panel header background -->
      <v-toolbar-title v-if="duty">
        <span v-if="!xs"> {{ $t('myDuty.widgetTitle') }}</span>
        <v-chip class="ml-4">{{ $t('myDuty.title') }} {{ duty.dutyname }}</v-chip>
      </v-toolbar-title>

      <v-toolbar-title class="subtitle-1" v-else> {{ $t('myDuty.noDuty') }}</v-toolbar-title>
      <v-checkbox
        class="ml-8 mt-4 caption align-center"
        rounded
        outlined
        text
        small
        style="width: 200px"
        :label="$t('myD.showTurnaroundTimes')"
        v-model="collapseAllDutyActivities"
      ></v-checkbox>

      <v-spacer></v-spacer>
      <date-list-picker
        v-model="selectedDutyIndex"
        v-if="availableDateIsoStringsSorted"
        :availableDateIsoStringsSorted="availableDateIsoStringsSorted"
      ></date-list-picker>
    </template>

    <template v-slot:content>
      <div class="scroll-container left-container" :style="`min-width:${xs ? '100vw' : '450px'}`">
        <duty-activities
          v-if="duty && dutyActivities.length > 0"
          :duty="duty"
          :dutyActivities="dutyActivities"
          :mergeAllDuties="collapseAllDutyActivities"
          @onDutyActivitySelected="displayDutyActivityTracks"
          @onDutyActivityLineSelected="displayDutyActivityTracksOfOneTrack"
        ></duty-activities>
        <info-card v-else :numberedTextRows="[$t('myDuty.dutySelectionInstruction')]">
          <template v-slot:title>{{ $t('myDuty.noDuty') }}</template>
        </info-card>
      </div>
      <div v-if="!xs" class="d-flex flex-grow-1 right-container">
        <perlschnur
          caller="myDuty"
          class="full-width"
          v-if="line && track"
          :scrollAreaPixelHeight="contentHeight - (xs ? 70 : 90)"
          :startToMinutesAfterMidnight="startToMinutesAfterMidnight"
          :endToMinutesAfterMidnight="endToMinutesAfterMidnight"
          :line="line"
          :track="track"
          :dateIsoString="dateIsoString"
          :dateString="dateString"
          :showHorizontalTimeline="false"
        ></perlschnur>
        <info-card v-else :numberedTextRows="[$t('myDuty.instruction1')]">
          <template v-slot:title>{{ $t('myDuty.noDutyActivityChosen') }}</template>
        </info-card>
      </div>
    </template>
  </panel>
</template>

<script>
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dutyMixin from '@/modules/myDuties/common/dutyMixin';
import dateUtils from '@/common/dateUtils';
import DutyActivities from '@/modules/myDuties/dutyActivities/DutyActivities.vue';
import { mapState } from 'vuex';
import InfoCard from '@/components/container/InfoCard';
import DateListPicker from '@/components/pickers/DateListPicker';
import Panel from '@/components/container/Panel.vue';
import Perlschnur from '@/modules/common/Perlschnur.vue';
import dateSorters from '@/sorters/dateSorters';

export default {
  name: 'OnDutyNotification',
  mixins: [common, configMixin, dutyMixin],
  props: {
    config: {
      type: Object,
    },
  },
  components: { DateListPicker, DutyActivities, Perlschnur, Panel, InfoCard },
  data() {
    return {
      dutyActivity: null,
      calculatedHeight: 530,

      endToMinutesAfterMidnight: null,
      startToMinutesAfterMidnight: null,
      line: null,
      track: null,
      dateIsoString: null,

      dayOffset: 0,
      collapseAllDutyActivities: true,
      selectedDutyIndex: 0,
      showAllActivitiesOfSelectedTrack: false,
    };
  },

  computed: {
    ...mapState({ tenant: (state) => state.profile.customerRelations.tenant }),
    ...mapState('meinDienst', ['duties']),
    contentHeight() {
      return this.calculatedHeight - 50;
    },

    sortedDuties() {
      if (this.duties == null) return [];
      let sortedDuties = dateSorters.sortByDateIsoStringProperty(this.duties, 'day');
      console.debug('sortedDuties', sortedDuties, this.duties);
      return sortedDuties;
    },
    availableDateIsoStringsSorted() {
      return this.sortedDuties.map((duty) => duty.day);
    },

    duty() {
      if (this.selectedDutyIndex === null || this.sortedDuties === null) return null;
      return this.sortedDuties[this.selectedDutyIndex];
    },

    dateString() {
      if (this.duty && this.duty.day) {
        return dateUtils.reformatDateIsoStringAsYYYYMMDD(this.duty.day);
      }
      return '';
    },

    dutyActivities() {
      let dutyActivities = this.mx_getDutyActivities(this.duty, {
        tenant: this.tenant,
      });

      return dutyActivities;
    },

    dateShort() {
      if (this.duty && this.duty.day) return dateUtils.formatAsLocaleNamedShortDate(dateUtils.parseISOString(this.duty.day), this.locale);
      console.debug('duty is undefined or duty.day', this.duty, 'selectedDutyIndex', this.selectedDutyIndex, 'sortedDuties', this.sortedDuties);
      return null;
    },

    hasNext() {
      if (this.sortedDuties) {
        return this.selectedDutyIndex < this.sortedDuties.length - 1;
      }
      return false;
    },
    color() {
      return this.darkMode ? '' : this.config.headerFgColor.light;
    },

    backgroundClasses() {
      return this.config.background ? this.config.background[this.darkMode ? 'dark' : 'light'] : '';
    },
  },
  methods: {
    /**
     * Display the tour associated with the duty, day, line and track
     */
    displayDutyActivityTracks(dutyActivity) {
      this.showAllActivitiesOfSelectedTrack = false;
      this.dutyActivity = dutyActivity;
      this.mx_isLeftDrawerOpen = false;
      console.debug('displayDutyActivityTracks', dutyActivity);
      this.displayMode = 'tour';
      let duty = this.duty;

      this.dateIsoString = duty.day;
      this.track = dutyActivity.course;
      this.line = dutyActivity.line;
      this.startToMinutesAfterMidnight = dutyActivity.fromMinutesAfterMidnight;
      this.endToMinutesAfterMidnight = dutyActivity.toMinutesAfterMidnight;

      let { dateIsoString, track, line, startToMinutesAfterMidnight, endToMinutesAfterMidnight } = this;
      console.debug(
        'selection',
        JSON.stringify(
          {
            dateIsoString,
            track,
            line,
            startToMinutesAfterMidnight,
            endToMinutesAfterMidnight,
          },
          null,
          2
        )
      );
    },
    displayDutyActivityTracksOfOneTrack(dutyActivity) {
      this.mx_isLeftDrawerOpen = false;
      console.debug('displayDutyActivityTracksOfOneTrack', dutyActivity);
      this.displayMode = 'tour';
      let duty = this.duty;

      this.dateIsoString = duty.day;
      this.track = dutyActivity.course;
      this.line = dutyActivity.line;
      let firstDutyActivity = this.dutyActivities.find((da) => da.line === this.line && da.course === this.track);
      let lastDutyActivity = this.dutyActivities
        .slice()
        .reverse()
        .find((da) => da.line === this.line && da.course === this.track);

      this.startToMinutesAfterMidnight = firstDutyActivity.fromMinutesAfterMidnight;
      this.endToMinutesAfterMidnight = lastDutyActivity.toMinutesAfterMidnight;

      let { dateIsoString, track, line, startToMinutesAfterMidnight, endToMinutesAfterMidnight } = this;
      console.debug(
        'selection',
        JSON.stringify(
          {
            dateIsoString,
            track,
            line,
            startToMinutesAfterMidnight,
            endToMinutesAfterMidnight,
          },
          null,
          2
        )
      );
    },
  },
  watch: {
    showAllActivitiesOfSelectedTrack(newValue, oldValue) {
      if (this.dutyActivity) {
        if (this.showAllActivitiesOfSelectedTrack) {
          this.displayDutyActivityTracksOfOneTrack(this.dutyActivity);
        } else {
          this.displayDutyActivityTracks(this.dutyActivity);
        }
      }
    },
    sortedDuties(newSortedDuties, oldSortedDuties) {
      // Find day that is next to today
      let today = Date.now();

      let nearestDateObj = dateUtils.selectNearestDate(
        newSortedDuties.map((d) => d.day),
        today
      );
      this.selectedDutyIndex = nearestDateObj.selectedIndex;
      console.debug('this.selectedDutyIndex', this.selectedDutyIndex, 'selectedDateAsIsoString', nearestDateObj.selectedDateAsIsoString);
    },
    selectedDutyIndex(newValue, oldValue) {
      this.line = null;
    },
  },
};
</script>
<style scoped>
.left-container {
  position: absolute;
  width: 30%;
  top: 60px;
  bottom: 0;
  left: 0;
}

.right-container {
  position: absolute;
  max-width: calc(100% - 450px);
  width: 70%;
  top: 54px;
  bottom: 0;
  right: 0;
}
</style>
